import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../providers/AuthProvider'
import { useExperiments } from '../../hooks/useExperiments'
import { useExpertReviews } from '../../hooks/useExpertReviews'
import { supabase } from '../../supabaseClient'
import { KnowledgeUnit } from '../../types'
import { coverageRate, llmExpertAgreement, confusionMatrix, crossLevelConsistency } from '../../lib/analysis'
import { exportKUsCSV, exportReviewsCSV, exportExperimentJSON, exportMetricsJSON } from '../../lib/export'

export default function ExperimentAnalysisPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { experiments } = useExperiments(user?.id || '')
  const experiment = experiments.find((e) => e.id === id)
  const { reviews, loading: reviewsLoading, load: loadReviews } = useExpertReviews(id)

  const [kus, setKUs] = useState<KnowledgeUnit[]>([])
  const [kusLoading, setKUsLoading] = useState(true)

  const loadKUs = useCallback(async () => {
    if (!id) return
    setKUsLoading(true)
    const { data } = await supabase.from('knowledge_units').select('*').eq('experiment_id', id).order('standard_id')
    setKUs((data ?? []) as KnowledgeUnit[])
    setKUsLoading(false)
  }, [id])

  useEffect(() => {
    loadKUs()
    loadReviews()
  }, [loadKUs, loadReviews])

  // ── Computed metrics ────────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    if (!experiment) return null
    const stdIds = experiment.standards
    const taxonomy = experiment.taxonomy
    const passIds = experiment.material_levels.map((ml) => ml.id)

    // Find the correctness and corrected_type dimension IDs
    const correctnessDim =
      experiment.evaluation_dimensions.find((d) => d.id === 'correctness' || d.id === 'existence') || null
    const correctedTypeDim = experiment.evaluation_dimensions.find((d) => d.type === 'taxonomy_select') || null

    const coverage = coverageRate(kus, stdIds)

    // Type distribution
    const typeDist: Record<string, number> = {}
    for (const t of taxonomy.types) typeDist[t] = 0
    for (const ku of kus) {
      const t = ku.proposed_type || 'unclear'
      typeDist[t] = (typeDist[t] || 0) + 1
    }

    // Agreement & confusion (only if reviews exist and dimensions configured)
    let agreement = null
    let confusion = null
    if (correctnessDim && correctedTypeDim && reviews.length > 0) {
      agreement = llmExpertAgreement(kus, reviews, correctnessDim.id, correctedTypeDim.id, taxonomy.types)
      confusion = confusionMatrix(kus, reviews, correctnessDim.id, correctedTypeDim.id, taxonomy.types)
    }

    // Cross-level consistency
    const crossLevel = crossLevelConsistency(kus, stdIds.slice(0, 20), passIds)

    // Ambiguity rate (from reviews — legacy dimension-keyed responses)
    const ambiguityDim = experiment.evaluation_dimensions.find((d) => d.id === 'ambiguity')
    let ambiguityRate = null
    if (ambiguityDim && reviews.length > 0) {
      const ambiguous = reviews.filter(
        (r) => (r.responses as unknown as Record<string, unknown>)[ambiguityDim.id] === 'ambiguous',
      ).length
      ambiguityRate = ambiguous / reviews.length
    }

    // Compound rate
    const compoundDim = experiment.evaluation_dimensions.find((d) => d.id === 'compound')
    let compoundRate = null
    if (compoundDim && reviews.length > 0) {
      const compounds = reviews.filter(
        (r) => (r.responses as unknown as Record<string, unknown>)[compoundDim.id] === true,
      ).length
      compoundRate = compounds / reviews.length
    }

    // Gap type distribution
    const gapDim = experiment.evaluation_dimensions.find((d) => d.id === 'gap_type')
    let gapDist: Record<string, number> | null = null
    if (gapDim && reviews.length > 0) {
      gapDist = {}
      for (const r of reviews) {
        const g = ((r.responses as unknown as Record<string, unknown>)[gapDim.id] as string) || 'none'
        gapDist[g] = (gapDist[g] || 0) + 1
      }
    }

    return { coverage, typeDist, agreement, confusion, crossLevel, ambiguityRate, compoundRate, gapDist }
  }, [experiment, kus, reviews])

  if (!experiment) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Experiment not found.</Typography>
      </Box>
    )
  }

  if (kusLoading || reviewsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!metrics) return null

  const pct = (n: number) => `${(n * 100).toFixed(1)}%`

  return (
    <Box sx={{ p: 2.5, overflowY: 'auto', height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', mb: 0.5, fontSize: 18 }}>
        {experiment.name} — Analysis
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, flex: 1 }}>
          {kus.length} KUs · {reviews.length} reviews · {experiment.standards.length} standards
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => exportKUsCSV(kus, `${experiment.name}_kus.csv`)}
          sx={{ fontSize: 10, fontWeight: 700 }}
        >
          Export KUs
        </Button>
        {reviews.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              exportReviewsCSV(reviews, experiment.evaluation_dimensions, `${experiment.name}_reviews.csv`)
            }
            sx={{ fontSize: 10, fontWeight: 700 }}
          >
            Export Reviews
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            exportExperimentJSON(experiment, `${experiment.name}_config.json`)
            if (metrics) exportMetricsJSON(metrics, `${experiment.name}_metrics.json`)
          }}
          sx={{ fontSize: 10, fontWeight: 700 }}
        >
          Export All
        </Button>
      </Box>

      {/* ── Summary cards ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5, mb: 3 }}>
        <MetricCard
          label="Coverage"
          value={pct(metrics.coverage.coverageRate)}
          sub={`${metrics.coverage.standardsWithKUs}/${metrics.coverage.totalStandards} standards`}
        />
        <MetricCard label="Total KUs" value={String(kus.length)} />
        <MetricCard label="Reviews" value={String(reviews.length)} />
        {metrics.agreement && (
          <>
            <MetricCard
              label="Agreement"
              value={pct(metrics.agreement.agreementRate)}
              sub={`${metrics.agreement.agreed}/${metrics.agreement.total}`}
            />
            <MetricCard label="Cohen's κ" value={metrics.agreement.kappa.toFixed(3)} />
          </>
        )}
        {metrics.ambiguityRate !== null && <MetricCard label="Ambiguity" value={pct(metrics.ambiguityRate)} />}
        {metrics.compoundRate !== null && <MetricCard label="Compound" value={pct(metrics.compoundRate)} />}
      </Box>

      {/* ── Type distribution ── */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
            Type Distribution (LLM)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {experiment.taxonomy.types.map((t) => (
              <Box key={t} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'primary.main' }}>
                  {metrics.typeDist[t] || 0}
                </Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}>{t}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ── Confusion matrix ── */}
      {metrics.confusion && metrics.confusion.total > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
              Confusion Matrix (LLM rows × Expert cols)
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '4px 8px', textAlign: 'left', color: '#64748b' }}>LLM ↓ / Expert →</th>
                    {metrics.confusion.labels.map((l) => (
                      <th key={l} style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 700 }}>
                        {l}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {metrics.confusion.labels.map((rowLabel, ri) => (
                    <tr key={rowLabel}>
                      <td style={{ padding: '4px 8px', fontWeight: 700 }}>{rowLabel}</td>
                      {metrics.confusion!.matrix[ri].map((val, ci) => {
                        const isDiag = ri === ci
                        return (
                          <td
                            key={ci}
                            style={{
                              padding: '4px 8px',
                              textAlign: 'center',
                              fontWeight: isDiag ? 800 : 400,
                              backgroundColor: isDiag ? '#e0f2fe' : val > 0 ? '#fef2f2' : 'transparent',
                            }}
                          >
                            {val}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Gap type distribution ── */}
      {metrics.gapDist && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
              Gap Type Distribution
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {Object.entries(metrics.gapDist).map(([type, count]) => (
                <Box key={type} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'primary.main' }}>{count}</Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}>{type}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Cross-level consistency (first 10 standards) ── */}
      {metrics.crossLevel.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
              Cross-Level Consistency (type distribution by pass)
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '3px 6px', textAlign: 'left' }}>Standard</th>
                    {experiment.material_levels.map((ml) => (
                      <th key={ml.id} style={{ padding: '3px 6px', textAlign: 'center' }}>
                        {ml.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {metrics.crossLevel.slice(0, 10).map((row) => (
                    <tr key={row.standardId}>
                      <td style={{ padding: '3px 6px', fontWeight: 600 }}>{row.standardId}</td>
                      {experiment.material_levels.map((ml) => {
                        const dist = row.typesByPass[ml.id] || {}
                        const summary = Object.entries(dist)
                          .filter(([, v]) => v > 0)
                          .map(([t, v]) => `${t[0].toUpperCase()}:${v}`)
                          .join(' ')
                        return (
                          <td key={ml.id} style={{ padding: '3px 6px', textAlign: 'center', color: '#64748b' }}>
                            {summary || '—'}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'primary.main' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  )
}
